import ArticleInterface from 'app/interfaces/articles/ArticleInterface';
import PromoNewInterface from 'app/interfaces/articles/PromoNewInterface';

const PROMO_NEW_LISTS = [
    'bets_promo',
    'casino_promo',
    'permanent_promo'
] as const;

const MILLISECONDS_PER_SECOND = 1000;

type PromoNewLists = Pick<PromoNewInterface, typeof PROMO_NEW_LISTS[number]>;

const promoNewListFor = (article: ArticleInterface, promos: ArticleInterface[], merged: ArticleInterface): ArticleInterface[] => {
    if (promos.some(promo => promo.id === article.id)) {
        return promos.map(promo => promo.id === article.id ? merged : promo);
    }

    return [merged, ...promos];
};

export const upsertPromoNewArticle = (data: PromoNewInterface, article: ArticleInterface): PromoNewInterface => {
    const listKey = `${article.promo_type}_promo`;
    const existing = PROMO_NEW_LISTS.flatMap(key => data[key]).find(promo => promo.id === article.id);
    const merged = {...existing, ...article};
    const lists = {} as PromoNewLists;

    PROMO_NEW_LISTS.forEach(key => {
        lists[key] = key === listKey
            ? promoNewListFor(article, data[key], merged)
            : data[key].filter(promo => promo.id !== article.id);
    });

    const viewTracked = !article.bonus_type || data.user_promo_view.some(view => view.id === article.id);

    return {
        ...data,
        ...lists,
        user_promo_view: viewTracked ? data.user_promo_view : [...data.user_promo_view, {id: article.id, viewed: false}]
    };
};

const isRestricted = (promo: ArticleInterface): boolean => Boolean(promo.available_for) && promo.available_for !== 'all_users';

const isExpired = (promo: ArticleInterface, now: number): boolean => Boolean(promo.ended_at) && promo.ended_at * MILLISECONDS_PER_SECOND <= now;

export const mergeGeneralPromoNewData = (data: PromoNewInterface, general: PromoNewInterface, now = Date.now()): PromoNewInterface => {
    const generalIds = new Set(PROMO_NEW_LISTS.flatMap(key => general[key]).map(promo => promo.id));
    const lists = {} as PromoNewLists;

    PROMO_NEW_LISTS.forEach(key => {
        const kept = data[key].filter(promo => isRestricted(promo) && !isExpired(promo, now) && !generalIds.has(promo.id));

        lists[key] = [...general[key], ...kept].sort((a, b) => b.date - a.date);
    });

    const feedIds = new Set(PROMO_NEW_LISTS.flatMap(key => lists[key]).map(promo => promo.id));
    const generalViewIds = new Set(general.user_promo_view.map(view => view.id));

    return {
        ...general,
        ...lists,
        user_promo_view: [...general.user_promo_view, ...data.user_promo_view.filter(view => feedIds.has(view.id) && !generalViewIds.has(view.id))]
    };
};

export const removePromoNewArticle = (data: PromoNewInterface, id: number): PromoNewInterface => {
    const lists = {} as PromoNewLists;

    PROMO_NEW_LISTS.forEach(key => {
        lists[key] = data[key].filter(promo => promo.id !== id);
    });

    return {
        ...data,
        ...lists,
        user_promo_view: data.user_promo_view.filter(view => view.id !== id)
    };
};
